import type { ReactElement } from 'react';
import { cn } from './cn';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  /**
   * Announced status text. Pass `null` for a purely decorative spinner — used
   * inside controls that already announce their busy state.
   */
  label?: string | null;
  className?: string;
}

const SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-8 w-8',
};

export function Spinner({ size = 'md', label = 'Loading', className }: SpinnerProps): ReactElement {
  const graphic = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={cn('animate-spin', SIZE[size], label === null ? className : undefined)}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );

  if (label === null) return graphic;

  return (
    <span role="status" className={cn('inline-flex items-center text-ink-muted', className)}>
      {graphic}
      <span className="fs-sr-only">{label}</span>
    </span>
  );
}
